// Auto-compound (phase 2) state for the connected user, shared by the staking
// cards. `config` is null until the contract has auto-compound (pre-upgrade) —
// callers hide every auto-restake element in that case. `available` is true when
// the feature is switched on, or when this user still has it on (so they can
// always turn it off).
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  AutoCompoundConfig,
  AutoCompoundInfo,
  sorobanService,
} from "../services/soroban.service";
import { signWithUserWallet, WALLET_WAIT_STOPPED } from "../utils/walletSign";
import { getErrorMessage, isUserRejection } from "../utils/errors";

// Every card mounting the hook refreshes when any of them changes the switch.
const listeners = new Set<() => void>();

export function useAutoCompound(address?: string | null, walletName?: string | null) {
  const [config, setConfig] = useState<AutoCompoundConfig | null>(null);
  const [info, setInfo] = useState<AutoCompoundInfo | null>(null);
  const [busy, setBusy] = useState(false);
  // True only while the wallet holds the signing request (shows "Waiting… Cancel").
  const [waitingForWallet, setWaitingForWallet] = useState(false);
  const cancelRef = useRef<(() => void) | null>(null);

  const refresh = useCallback(async () => {
    if (!address) {
      setConfig(null);
      setInfo(null);
      return;
    }
    const [c, i] = await Promise.all([
      sorobanService.queryAutoCompoundConfig(address),
      sorobanService.queryAutoCompoundInfo(address),
    ]);
    setConfig(c);
    setInfo(i);
  }, [address]);

  useEffect(() => {
    refresh();
    listeners.add(refresh);
    return () => {
      listeners.delete(refresh);
    };
  }, [refresh]);

  const setEnabled = useCallback(
    async (enabled: boolean): Promise<boolean> => {
      if (!address) {
        toast.error("Please connect your wallet");
        return false;
      }
      setBusy(true);
      try {
        const { transaction } = await sorobanService.buildSetAutoCompoundTransaction(address, enabled);
        const cancel = new Promise<void>((resolve) => {
          cancelRef.current = resolve;
        });
        setWaitingForWallet(true);
        let signed: string;
        try {
          signed = await signWithUserWallet(walletName, address, transaction.toXDR(), cancel);
        } finally {
          setWaitingForWallet(false);
          cancelRef.current = null;
        }
        const result = await sorobanService.submitSignedTransaction(signed);
        if (!result.success) throw new Error(result.error || "Transaction failed");
        toast.success(enabled ? "Auto-restake is on." : "Auto-restake is off. Claim or restake manually.");
        listeners.forEach((l) => l());
        return true;
      } catch (err) {
        if ((err as any)?.message === WALLET_WAIT_STOPPED) return false; // the user pressed Cancel
        const reason = getErrorMessage(err, "Could not change auto-restake.");
        toast.error(isUserRejection(err) ? reason : `Auto-restake: ${reason}`);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [address, walletName]
  );

  const cancelWait = useCallback(() => cancelRef.current?.(), []);

  const available = !!config && (config.enabled || !!info?.enabled);
  return { config, info, busy, waitingForWallet, cancelWait, available, refresh, setEnabled };
}
